import { useCallback } from 'react';
import useSWR, { SWRResponse, Key, useSWRConfig } from 'swr';
import { AxiosError } from 'axios';

import {
  ReservationFlowPaymentMethodListSubscription,
  ReservationFlowCustomerSubscriptionAvailableReservationCount,
} from '../../../../interfaces/reservation_flow';
import { useClientSideApiClient } from '../../../../openapi/axiosClient';
import { captureException } from '@sentry/nextjs';

export interface UseGetReservationFlowSubscriptionAvailableReservationCountDataParams {
  parameter?: {
    merchantPublicId: string;
    paymentMethodSubscription: ReservationFlowPaymentMethodListSubscription;
    checkin: string;
  };
  isDisabled?: boolean;
}

export type UseGetReservationFlowSubscriptionAvailableReservationCountDataValue =
  SWRResponse<
    ReservationFlowCustomerSubscriptionAvailableReservationCount[],
    AxiosError
  >;

export const getGetReservationFlowSubscriptionAvailableReservationCountDataKey: (params: {
  parameter: UseGetReservationFlowSubscriptionAvailableReservationCountDataParams['parameter'];
}) => Key = ({ parameter }) => {
  return {
    path: '/api/reservation_flow/merchants/{merchant_public_id}/customer_subscriptions/{customer_subscription_public_id}/available_reservation_count',
    parameter,
  };
};

export const useGetReservationFlowSubscriptionAvailableReservationCountData: (
  params: UseGetReservationFlowSubscriptionAvailableReservationCountDataParams,
) => UseGetReservationFlowSubscriptionAvailableReservationCountDataValue = ({
  parameter,
  isDisabled,
}) => {
  const apiClient = useClientSideApiClient();
  const key =
    !isDisabled && typeof parameter !== 'undefined'
      ? getGetReservationFlowSubscriptionAvailableReservationCountDataKey({
          parameter,
        })
      : null;

  const fetcher: () => Promise<
    ReservationFlowCustomerSubscriptionAvailableReservationCount[]
  > = async () => {
    if (typeof parameter === 'undefined') {
      captureException('Never');
      throw new Error('Never');
    }

    const { merchantPublicId, paymentMethodSubscription, checkin } = parameter;

    const customerSubscriptionPublicIds =
      paymentMethodSubscription.products.flatMap((product) => {
        return product.active_customer_subscriptions.map(
          (activeCustomerSubscription) => {
            return activeCustomerSubscription.public_id;
          },
        );
      });

    try {
      return await Promise.all(
        customerSubscriptionPublicIds.map(async (publicId) => {
          const value =
            await apiClient.getReservationFlowCustomerSubscriptionReservationAvailableCount(
              {
                parameter: {
                  merchant_public_id: merchantPublicId,
                  customer_subscription_public_id: publicId,
                  checkin,
                },
              },
            );

          return {
            checkin,
            publicId,
            value,
          };
        }),
      );
    } catch (error) {
      throw error;
    }
  };

  return useSWR(key, fetcher);
};

export type UseClearAllGetReservationFlowSubscriptionAvailableReservationCountDataParams =
  {
    parameter: {
      merchantPublicId: NonNullable<
        UseGetReservationFlowSubscriptionAvailableReservationCountDataParams['parameter']
      >['merchantPublicId'];
    };
  };

export type UseClearAllGetReservationFlowSubscriptionAvailableReservationCountDataValue =
  {
    clearAllGetReservationFlowSubscriptionAvailableReservationCountData: () => Promise<void>;
  };

export const useClearAllGetReservationFlowSubscriptionAvailableReservationCountData: (
  params: UseClearAllGetReservationFlowSubscriptionAvailableReservationCountDataParams,
) => UseClearAllGetReservationFlowSubscriptionAvailableReservationCountDataValue = ({
  parameter: { merchantPublicId },
}) => {
  const { mutate } = useSWRConfig();

  const clearAllGetReservationFlowSubscriptionAvailableReservationCountData =
    useCallback(async () => {
      await mutate(
        (key) => {
          if (!key || typeof key !== 'object') {
            return false;
          }

          if (
            !('path' in key) ||
            !('parameter' in key) ||
            !('merchantPublicId' in key.parameter)
          ) {
            return false;
          }

          return (
            key.path ===
              '/api/reservation_flow/merchants/{merchant_public_id}/customer_subscriptions/{customer_subscription_public_id}/available_reservation_count' &&
            key.parameter.merchantPublicId === merchantPublicId
          );
        },
        undefined,
        { revalidate: false },
      );
    }, [merchantPublicId, mutate]);

  return {
    clearAllGetReservationFlowSubscriptionAvailableReservationCountData,
  };
};
